import { html, raw } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress } from '../lib/store.js';
import { syncState } from '../lib/sync.js';
import {
  getIndex, chapter, overallStats, nextStop, chapterStats, chapterSky, lessonMinutes, skyAt, inkOn,
} from '../lib/content.js';
import { streaks, lastWeek, localDay } from '../../shared/progress.js';
import { greeting, plural, percent, weekdayLetter, weekdayName } from '../lib/format.js';
import { dawnScene } from '../components/dawn.js';
import { topbar, avatar, SPEAKERS } from '../components/chrome.js';
import { saveCard, bindSaveCard } from '../components/save-card.js';

function lessonLabel(l) {
  if (l.ch === 0) return `Background · ${l.n} of 4`;
  const c = chapter(l.ch);
  return `Chapter ${l.ch} · Lesson ${l.n} of ${c.lessons.length}`;
}

function continueCard(stop, progress) {
  if (!stop) {
    return html`<section class="card continue continue--done" aria-labelledby="continue-title">
      <p class="eyebrow">Journey complete</p>
      <h2 class="h2" id="continue-title">You have read the whole Gita</h2>
      <p class="muted">All 18 chapters and 700 verses. Revisit any chapter, or start again from the beginning to see how differently it reads the second time.</p>
      <a class="btn btn--primary btn--block" href="/journey">Revisit the journey</a>
    </section>`;
  }
  const l = stop.lesson;
  const sky = chapterSky(l.ch);
  const first = Object.keys(progress.lessons).length === 0 && !stop.resuming;
  return html`<section class="card continue" aria-labelledby="continue-title" style="--sky-top:${sky.top};--sky-bottom:${sky.bottom}">
    <p class="eyebrow">${first ? 'Start here' : stop.resuming ? 'Pick up where you left off' : 'Up next'}</p>
    <h2 class="continue__title" id="continue-title">${l.title}</h2>
    <p class="continue__idea">${l.idea}</p>
    <p class="continue__meta">
      <span>${lessonLabel(l)}</span>
      <span class="continue__time">${icon('clock', { size: 15 })}${lessonMinutes(l)} min</span>
    </p>
    <a class="btn btn--primary btn--block continue__cta" href="/lesson/${l.id}">
      ${first ? 'Begin the journey' : stop.resuming ? 'Continue lesson' : 'Start lesson'} ${icon('arrow-right', { size: 20 })}
    </a>
  </section>`;
}

function journeyStrip(progress) {
  const idx = getIndex();
  const stats = overallStats(progress);
  const pct = percent(stats.done, stats.total);
  return html`<section class="section" aria-labelledby="journey-title">
    <div class="section__head">
      <h2 class="h3" id="journey-title">Your journey</h2>
      <a class="link" href="/journey">All chapters ${icon('chevron-right', { size: 16 })}</a>
    </div>
    <a class="card card--pad journey-card" href="/journey" aria-label="Your journey: ${pct} percent complete. ${stats.done} of ${stats.total} lessons.">
      <div class="journey-card__top">
        <p class="journey-card__pct num">${pct}<span>%</span></p>
        <dl class="journey-card__stats">
          <div><dt>Lessons</dt><dd class="num">${stats.done}<span> / ${stats.total}</span></dd></div>
          <div><dt>Verses</dt><dd class="num">${stats.verses}<span> / 700</span></dd></div>
          <div><dt>Chapters</dt><dd class="num">${stats.chaptersDone}<span> / 18</span></dd></div>
        </dl>
      </div>
      <div class="strip" aria-hidden="true">
        ${idx.chapters.map((c) => {
          const s = chapterStats(progress, c.ch);
          const f = s.total ? s.done / s.total : 0;
          return html`<span class="strip__seg" style="--seg:${chapterSky(c.ch).top}"><span class="strip__fill" style="width:${Math.round(f * 100)}%"></span></span>`;
        })}
      </div>
      <p class="journey-card__caption"><span>Night</span><span>Chapter 1 to 18</span><span>Morning</span></p>
    </a>
  </section>`;
}

function weekStrip(progress) {
  const week = lastWeek(progress.days);
  const st = streaks(progress.days);
  const line = st.current > 0
    ? `${plural(st.current, 'day')} in a row${st.best > st.current ? ` · best ${st.best}` : ''}`
    : st.best > 0 ? `Best run: ${plural(st.best, 'day')}` : 'A few minutes a day adds up';
  return html`<section class="section" aria-labelledby="week-title">
    <div class="section__head"><h2 class="h3" id="week-title">This week</h2><span class="small muted">${line}</span></div>
    <ol class="week card">
      ${week.map((d) => html`<li class="week__day${d.active ? ' is-active' : ''}${d.isToday ? ' is-today' : ''}">
        <span class="week__letter" aria-hidden="true">${weekdayLetter(d.day)}</span>
        <span class="week__dot" aria-hidden="true">${d.active ? icon('check', { size: 16, stroke: 2.5 }) : ''}</span>
        <span class="sr-only">${weekdayName(d.day)}${d.isToday ? ' (today)' : ''}: ${d.active ? 'studied' : 'no study'}</span>
      </li>`)}
    </ol>
  </section>`;
}

function verseOfTheDay() {
  const famous = getIndex().famous;
  if (!famous.length) return '';
  const dayNumber = Math.floor(new Date(`${localDay()}T12:00:00Z`).getTime() / 86400000);
  const f = famous[dayNumber % famous.length];
  const s = SPEAKERS[f.speaker];
  return html`<section class="section" aria-labelledby="votd-title">
    <div class="section__head"><h2 class="h3" id="votd-title">A verse for today</h2></div>
    <a class="card votd" href="/verse/${f.ch}/${f.v}">
      <p class="votd__tr">${f.translation}</p>
      <p class="votd__foot">${avatar(f.speaker)}<span>${s ? s.name : ''} · ${f.id}</span>${icon('arrow-right', { size: 18 })}</p>
    </a>
  </section>`;
}

function exploreLinks() {
  return html`<section class="section" aria-labelledby="more-title">
    <div class="section__head"><h2 class="h3" id="more-title">Explore</h2></div>
    <div class="tiles">
      <a class="tile" href="/guide"><span class="tile__icon">${icon('guide', { size: 22 })}</span><span class="tile__title">How it works</span><span class="tile__sub">Chapters, lessons and verses</span></a>
      <a class="tile" href="/people"><span class="tile__icon">${icon('people', { size: 22 })}</span><span class="tile__title">Who's who</span><span class="tile__sub">The people of the Gita</span></a>
      <a class="tile" href="/concepts"><span class="tile__icon">${icon('idea', { size: 22 })}</span><span class="tile__title">Key ideas</span><span class="tile__sub">Dharma, karma, yoga and more</span></a>
      <a class="tile" href="/explore#famous"><span class="tile__icon">${icon('quote', { size: 22 })}</span><span class="tile__title">Famous verses</span><span class="tile__sub">The lines people quote</span></a>
    </div>
  </section>`;
}

export default async function today() {
  const progress = getProgress();
  const stats = overallStats(progress);
  const stop = nextStop(progress);
  const st = streaks(progress.days);
  const user = syncState().user;
  const name = (user && user.name) || (progress.profile && progress.profile.name) || '';
  const skyInk = inkOn(skyAt(stats.fraction * 0.82));
  const meta = st.studiedToday
    ? 'You have studied today. Well done.'
    : st.current > 0 ? `Keep your ${plural(st.current, 'day')} going` : stats.done ? 'Welcome back' : 'The Bhagavad Gita, one short lesson at a time';

  return {
    title: 'Today',
    tint: skyAt(stats.fraction * 0.82),
    html: html`<div class="today">
      <section class="today__sky" style="--sky-ink:${skyInk}">
        ${dawnScene(stats.fraction, { className: 'today__dawn', label: `Your dawn: ${percent(stats.done, stats.total)} percent of the way through the Gita` })}
        ${topbar({ clear: true, showBrand: true })}
        <div class="today__hello wrap">
          <h1 class="today__greeting">${greeting()}${name ? raw(`,<br class="br-mobile"> `) : ''}${name}</h1>
          <p class="today__meta">${st.current > 0 ? icon('flame', { size: 18 }) : ''}<span>${meta}</span></p>
        </div>
      </section>
      <div class="wrap today__body">
        ${continueCard(stop, progress)}
        ${saveCard()}
        ${journeyStrip(progress)}
        ${weekStrip(progress)}
        ${verseOfTheDay()}
        ${exploreLinks()}
      </div>
    </div>`,
    mount(root) {
      bindSaveCard(root);
    },
  };
}
